import { Injectable } from '@angular/core';
import { Observable, from } from 'rxjs';
import { map, catchError } from 'rxjs/operators';
import { 
  getStorage, 
  ref, 
  uploadBytes, 
  getDownloadURL, 
  deleteObject,
  StorageReference,
  uploadBytesResumable 
} from 'firebase/storage';
import { getApp } from 'firebase/app';
import { storage } from '../config/firebase.config';

export interface UploadResult {
  success: boolean;
  filePath?: string;
  error?: string;
  downloadURL?: string;
}

export interface UploadProgress {
  bytesTransferred: number;
  totalBytes: number;
  percentage: number;
}

@Injectable({
  providedIn: 'root'
})
export class FileUploadService {
  private storage = storage; // Use the imported storage instance

  constructor() { }

  /**
   * Upload a file to Firebase Storage
   * Images are stored in Firebase Storage and accessible from your hosted app
   */
  uploadFile(file: File): Observable<UploadResult> {
    return from(this.uploadFileToFirebase(file));
  }

  /**
   * Upload a file to a specific folder in Firebase Storage
   */
  uploadFileToFolder(file: File, folder: string): Observable<UploadResult> {
    return from(this.uploadFileToFirebase(file, folder));
  }

  /**
   * Upload a file to Firebase Storage with progress tracking
   */
  uploadFileWithProgress(file: File): Observable<{ result: UploadResult; progress: UploadProgress }> {
    return new Observable(observer => {
      this.uploadFileToFirebaseWithProgress(file, observer);
    });
  }

  /**
   * Upload a file to a specific folder with progress tracking
   */
  uploadFileToFolderWithProgress(
    file: File,
    folder: string
  ): Observable<{ result: UploadResult; progress: UploadProgress }> {
    return new Observable(observer => {
      this.uploadFileToFirebaseWithProgress(file, observer, 0, folder);
    });
  }

  /**
   * Upload multiple files
   */
  uploadMultipleFiles(files: File[]): Observable<UploadResult[]> {
    const uploadPromises = files.map(file => this.uploadFile(file).toPromise());
    return from(Promise.all(uploadPromises).then(results => results.filter((result): result is UploadResult => result !== undefined)));
  }

  /**
   * Upload multiple files with progress tracking
   */
  uploadMultipleFilesWithProgress(files: File[]): Observable<{ result: UploadResult; progress: UploadProgress; fileIndex: number }> {
    return new Observable(observer => {
      files.forEach((file, index) => {
        this.uploadFileToFirebaseWithProgress(file, observer, index);
      });
    });
  }

  /**
   * Upload file to Firebase Storage
   */
  private async uploadFileToFirebase(file: File, folder: string = 'product-images'): Promise<UploadResult> {
    try {
      // Validate storage instance
      if (!this.storage) {
        console.error('Firebase Storage not initialized');
        return {
          success: false,
          error: 'Firebase Storage not initialized'
        };
      }

      // Create a unique filename while preserving original name
      const timestamp = Date.now();
      const originalName = file.name;
      const extension = originalName.split('.').pop();
      const nameWithoutExt = originalName.replace(`.${extension}`, '');
      
      // Format: timestamp_originalname.extension
      const finalFilename = `${timestamp}_${nameWithoutExt}.${extension}`;
      
      // Create Firebase Storage reference
      const sanitizedFolder = folder.replace(/^[\/]+|[\/]+$/g, '');
      const storageRef = ref(this.storage, `${sanitizedFolder}/${finalFilename}`);
      
      console.log(`Uploading ${finalFilename} to Firebase Storage...`);
      console.log('Storage instance:', this.storage);
      console.log('Storage reference:', storageRef);
      console.log('File details:', {
        name: file.name,
        size: file.size,
        type: file.type
      });
      
      // Upload file to Firebase Storage with metadata
      const metadata = {
        contentType: file.type,
        cacheControl: 'public, max-age=31536000', // 1 year cache
      };
      
      const snapshot = await uploadBytes(storageRef, file, metadata);
      console.log(`File uploaded successfully: ${finalFilename}`);
      
      // Get download URL
      const downloadURL = await getDownloadURL(snapshot.ref);
      console.log(`Download URL: ${downloadURL}`);
      
      return {
        success: true,
        filePath: `${sanitizedFolder}/${finalFilename}`,
        downloadURL: downloadURL
      };
    } catch (error) {
      console.error('Error uploading file to Firebase:', error);
      console.error('Error details:', {
        name: (error as any)?.name,
        message: (error as any)?.message,
        code: (error as any)?.code,
        stack: (error as any)?.stack
      });
      
      // Check if it's a CORS error
      if (error && typeof error === 'object' && 'message' in error && 
          typeof error.message === 'string' && error.message.includes('CORS')) {
        return {
          success: false,
          error: 'CORS error: Please check Firebase Storage rules and CORS configuration'
        };
      }
      
      // Check if it's a 404 error
      if (error && typeof error === 'object' && 'code' in error && 
          error.code === 'storage/not-found') {
        return {
          success: false,
          error: 'Firebase Storage not found. Please check your Firebase configuration and ensure Storage is enabled.'
        };
      }
      
      // Check if it's a storage/unknown error
      if (error && typeof error === 'object' && 'code' in error && 
          error.code === 'storage/unknown') {
        return {
          success: false,
          error: 'Firebase Storage configuration error. Please check Storage rules and ensure write permissions are enabled.'
        };
      }
      
      return {
        success: false,
        error: `Failed to upload file to Firebase: ${(error as any)?.message || 'Unknown error'}`
      };
    }
  }

  /**
   * Upload file to Firebase Storage with progress tracking
   */
  private async uploadFileToFirebaseWithProgress(
    file: File, 
    observer: any, 
    fileIndex: number = 0,
    folder: string = 'product-images'
  ): Promise<void> {
    try {
      // Validate storage instance
      if (!this.storage) {
        observer.next({
          result: {
            success: false,
            error: 'Firebase Storage not initialized'
          },
          progress: { bytesTransferred: 0, totalBytes: file.size, percentage: 0 },
          fileIndex
        });
        observer.complete();
        return;
      }

      // Create a unique filename while preserving original name
      const timestamp = Date.now();
      const originalName = file.name;
      const extension = originalName.split('.').pop();
      const nameWithoutExt = originalName.replace(`.${extension}`, '');
      
      // Format: timestamp_originalname.extension
      const finalFilename = `${timestamp}_${nameWithoutExt}.${extension}`;
      
      // Create Firebase Storage reference
      const sanitizedFolder = folder.replace(/^[\\/]+|[\\/]+$/g, '');
      const storageRef = ref(this.storage, `${sanitizedFolder}/${finalFilename}`);
      
      console.log(`Uploading ${finalFilename} to Firebase Storage with progress tracking...`);
      
      // Upload file to Firebase Storage with progress tracking
      const metadata = {
        contentType: file.type,
        cacheControl: 'public, max-age=31536000', // 1 year cache
      };
      
      const uploadTask = uploadBytesResumable(storageRef, file, metadata);
      
      // Monitor upload progress
      uploadTask.on('state_changed',
        (snapshot) => {
          const progress = {
            bytesTransferred: snapshot.bytesTransferred,
            totalBytes: snapshot.totalBytes,
            percentage: Math.round((snapshot.bytesTransferred / snapshot.totalBytes) * 100)
          };
          
          observer.next({
            result: { success: false, filePath: '', error: '' }, // Placeholder during progress
            progress,
            fileIndex
          });
        },
        (error) => {
          console.error('Upload error:', error);
          observer.next({
            result: {
              success: false,
              error: `Upload failed: ${error.message}`
            },
            progress: { bytesTransferred: 0, totalBytes: file.size, percentage: 0 },
            fileIndex
          });
        },
        async () => {
          // Upload completed successfully
          try {
            const downloadURL = await getDownloadURL(uploadTask.snapshot.ref);
            console.log(`File uploaded successfully: ${finalFilename}`);
            
            observer.next({
              result: {
                success: true,
                filePath: `${sanitizedFolder}/${finalFilename}`,
                downloadURL: downloadURL
              },
              progress: { bytesTransferred: file.size, totalBytes: file.size, percentage: 100 },
              fileIndex
            });
          } catch (error) {
            observer.next({
              result: {
                success: false,
                error: `Failed to get download URL: ${(error as any)?.message || 'Unknown error'}`
              },
              progress: { bytesTransferred: 0, totalBytes: file.size, percentage: 0 },
              fileIndex
            });
          }
        }
      );
    } catch (error) {
      console.error('Error in progress upload:', error);
      observer.next({
        result: {
          success: false,
          error: `Upload failed: ${(error as any)?.message || 'Unknown error'}`
        },
        progress: { bytesTransferred: 0, totalBytes: file.size, percentage: 0 },
        fileIndex
      });
    }
  }



  /**
   * Upload file to backend server (for production use)
   */
  private async uploadToBackend(file: File, filename: string): Promise<void> {
    // This is how you would implement actual file upload in production
    
    // 1. Create FormData
    const formData = new FormData();
    formData.append('file', file);
    formData.append('filename', filename);
    formData.append('folder', 'product');
    
    // 2. Send to backend API
    // const response = await fetch('/api/upload', {
    //   method: 'POST',
    //   body: formData
    // });
    
    // 3. Handle response
    // if (!response.ok) {
    //   throw new Error('Upload failed');
    // }
    
    // 4. Backend would save file to public/product/ folder
    // and return the file path
  }

  /**
   * Delete a file from Firebase Storage
   */
  deleteFile(filePath: string): Observable<boolean> {
    return from(this.deleteFileFromFirebase(filePath));
  }

  /**
   * Check Firebase Storage status
   */
  checkStorageStatus(): { initialized: boolean; bucket: string | null; error?: string } {
    try {
      if (!this.storage) {
        return { initialized: false, bucket: null, error: 'Storage not initialized' };
      }
      
      const bucket = this.storage.app.options.storageBucket || null;
      return { initialized: true, bucket };
    } catch (error) {
      return { initialized: false, bucket: null, error: 'Storage check failed' };
    }
  }

  /**
   * Delete file from Firebase Storage
   */
  private async deleteFileFromFirebase(filePath: string): Promise<boolean> {
    try {
      // Create reference to the file in Firebase Storage
      const storageRef = ref(this.storage, filePath);
      
      // Delete the file
      await deleteObject(storageRef);
      console.log(`File deleted successfully: ${filePath}`);
      
      return true;
    } catch (error) {
      console.error('Error deleting file from Firebase:', error);
      return false;
    }
  }


}
